'use client';

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { resolveMediaUrl } from '@/lib/api';
import { computeImageStyle } from '@/lib/imageStyle';
import { applyTextStyle } from '@/lib/textStyle';

/**
 * HeroBlock — bandeau d'accroche principal.
 * Variants : left-image | right-image | centered | fullwidth-bg
 */
export default function HeroBlock({ config }) {
  const variant = config.variant || 'left-image';
  const imageUrl = resolveMediaUrl(config.image_url);

  if (variant === 'centered') return <CenteredHero config={config} imageUrl={imageUrl} />;
  if (variant === 'fullwidth-bg') return <FullwidthHero config={config} imageUrl={imageUrl} />;

  // left-image / right-image (défaut)
  const isImageRight = variant === 'left-image';
  return (
    <section style={{
      paddingTop: '44px', paddingBottom: '44px',
      background: 'linear-gradient(180deg, var(--color-bg-alt) 0%, var(--color-bg) 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <BackgroundDecorations />
      <div className="container-page" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: imageUrl ? (isImageRight ? '1.1fr 1fr' : '1fr 1.1fr') : '1fr',
          gap: '60px',
          alignItems: 'center',
        }} className="hero-grid">
          {!isImageRight && imageUrl && <HeroImage url={imageUrl} imageStyle={config.image_style} />}
          <div>
            <HeroBadge text={config.badge_text} textStyle={config.badge_text_style} />
            <HeroTitle textStyle={config.title_style}>{config.title}</HeroTitle>
            <HeroSubtitle textStyle={config.subtitle_style}>{config.subtitle}</HeroSubtitle>
            {/* V1.25.951 — wrapper flex autour des CTAs : sans ca, les 2 boutons
                etaient colles (display: block parent → pas de gap). Ajout aussi
                de marginTop pour separer du subtitle. */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '32px' }}>
              <HeroCTAs primary={config.cta_primary} secondary={config.cta_secondary} />
            </div>
            <HeroReassurance />
          </div>
          {isImageRight && imageUrl && <HeroImage url={imageUrl} imageStyle={config.image_style} />}
        </div>
      </div>
    </section>
  );
}

function CenteredHero({ config, imageUrl }) {
  return (
    <section style={{
      paddingTop: '56px', paddingBottom: imageUrl ? '0' : '56px',
      background: 'linear-gradient(180deg, var(--color-bg-alt) 0%, var(--color-bg) 100%)',
      textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}>
      <BackgroundDecorations />
      <div className="container-page" style={{ position: 'relative', zIndex: 1, maxWidth: '900px' }}>
        <HeroBadge text={config.badge_text} textStyle={config.badge_text_style} centered />
        <HeroTitle large textStyle={config.title_style}>{config.title}</HeroTitle>
        <HeroSubtitle centered textStyle={config.subtitle_style}>{config.subtitle}</HeroSubtitle>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <HeroCTAs primary={config.cta_primary} secondary={config.cta_secondary} />
        </div>
      </div>
      {imageUrl && (
        <div style={{ marginTop: '60px', padding: '0 24px' }}>
          <img src={imageUrl} alt="" style={{
            maxWidth: '1100px', width: '100%', margin: '0 auto', display: 'block',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -20px 60px rgba(0,0,0,0.15)',
          }} />
        </div>
      )}
    </section>
  );
}

function FullwidthHero({ config, imageUrl }) {
  return (
    <section style={{
      minHeight: '70vh',
      paddingTop: '56px', paddingBottom: '56px',
      background: imageUrl
        ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${imageUrl}) center/cover`
        : 'var(--color-primary)',
      color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center',
    }}>
      <div className="container-page" style={{ maxWidth: '900px' }}>
        {config.badge_text && (
          <span style={applyTextStyle(config.badge_text_style, {
            display: 'inline-block', padding: '6px 14px',
            borderRadius: '999px',
            background: 'rgba(255,255,255,0.18)',
            color: '#fff',
            fontSize: '13px', fontWeight: 600, marginBottom: '20px',
          })}>{config.badge_text}</span>
        )}
        <h1 style={applyTextStyle(config.title_style, {
          fontSize: 'clamp(36px, 6vw, 64px)',
          fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: '0 0 24px', color: '#fff',
        })}>{config.title}</h1>
        <p style={applyTextStyle(config.subtitle_style, {
          fontSize: 'clamp(16px, 2vw, 20px)', lineHeight: 1.6,
          color: 'rgba(255,255,255,0.92)', maxWidth: '700px',
          margin: '0 auto 36px',
        })}>{config.subtitle}</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <HeroCTAs primary={config.cta_primary} secondary={config.cta_secondary} onDark />
        </div>
      </div>
    </section>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function BackgroundDecorations() {
  return (
    <>
      <div style={{
        position: 'absolute', top: '-200px', right: '-200px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'var(--color-primary)', opacity: 0.08,
        filter: 'blur(80px)', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-150px', left: '-150px',
        width: '400px', height: '400px', borderRadius: '50%',
        background: 'var(--color-secondary)', opacity: 0.06,
        filter: 'blur(80px)', pointerEvents: 'none',
      }} />
    </>
  );
}

function HeroBadge({ text, textStyle, centered }) {
  if (!text) return null;
  return (
    <div style={applyTextStyle(textStyle, {
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      padding: '6px 14px', borderRadius: '999px',
      background: 'rgba(226, 114, 40, 0.1)',
      color: 'var(--color-primary)',
      fontSize: '13px', fontWeight: 600,
      marginBottom: '24px',
      ...(centered ? {} : {}),
    })}>
      <CheckCircle2 size={14} />
      {text}
    </div>
  );
}

function HeroTitle({ children, large, textStyle }) {
  return (
    <h1 style={applyTextStyle(textStyle, {
      fontSize: large ? 'clamp(40px, 6.5vw, 72px)' : 'clamp(36px, 5.5vw, 64px)',
      fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em',
      margin: '0 0 24px', color: 'var(--color-text)',
    })}>{children}</h1>
  );
}

function HeroSubtitle({ children, centered, textStyle }) {
  return (
    <p style={applyTextStyle(textStyle, {
      fontSize: 'clamp(16px, 2vw, 20px)', lineHeight: 1.6,
      color: 'var(--color-text-muted)',
      margin: '0 0 36px', maxWidth: '600px',
      ...(centered ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
    })}>{children}</p>
  );
}

function HeroCTAs({ primary, secondary, onDark }) {
  return (
    <>
      {primary?.text && (
        <a
          href={primary.url || '#packs'}
          className="btn-primary"
          style={applyTextStyle(primary.text_style, { fontSize: '17px', padding: '16px 28px' })}
        >
          {primary.text} <ArrowRight size={18} />
        </a>
      )}
      {secondary?.text && (
        <a
          href={secondary.url || '#features'}
          style={applyTextStyle(secondary.text_style, {
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            fontSize: '17px', padding: '14px 26px',
            border: `2px solid ${onDark ? 'rgba(255,255,255,0.4)' : 'var(--color-primary)'}`,
            borderRadius: '12px',
            color: onDark ? '#fff' : 'var(--color-primary)',
            fontWeight: 600,
            background: 'transparent',
            textDecoration: 'none',
          })}
        >
          {secondary.text}
        </a>
      )}
    </>
  );
}

function HeroReassurance() {
  return (
    <div style={{
      marginTop: '32px', display: 'flex', flexWrap: 'wrap', gap: '20px',
      fontSize: '14px', color: 'var(--color-text-muted)',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <CheckCircle2 size={16} color="var(--color-primary)" /> Sans engagement
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <CheckCircle2 size={16} color="var(--color-primary)" /> Essai gratuit
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <CheckCircle2 size={16} color="var(--color-primary)" /> Support FR
      </span>
    </div>
  );
}

function HeroImage({ url, imageStyle }) {
  const { wrapperStyle, imgStyle } = computeImageStyle(imageStyle);
  return (
    <div style={{ position: 'relative', ...wrapperStyle }}>
      <img src={url} alt="" style={imgStyle} />
    </div>
  );
}
